import React, { useState, useEffect, useRef } from "react";
// New York - Originals
import ny1 from './assets/Photography/New York/DSCF0517.JPG';
import ny2 from './assets/Photography/New York/DSCF0577.jpg';
import ny3 from './assets/Photography/New York/DSCF0588.jpg';
// New York - Compressed
import ny1Thumb from './assets/Photography/New York/DSCF0517_thumb.JPG';
import ny2Thumb from './assets/Photography/New York/DSCF0577_thumb.jpg';
import ny3Thumb from './assets/Photography/New York/DSCF0588_thumb.jpg';
// Beijing - Originals
import beijing1 from './assets/Photography/Beijing/Beijing1.JPG';
import beijing2 from './assets/Photography/Beijing/Beijing2.jpg';
// Beijing - Compressed
import beijing1Thumb from './assets/Photography/Beijing/Beijing1_thumb.JPG';
import beijing2Thumb from './assets/Photography/Beijing/Beijing2_thumb.jpg';
// Norway - Originals
import norway1 from './assets/Photography/Norway/Norway1.JPG';
import norway2 from './assets/Photography/Norway/Norway2.JPG';
import norway3 from './assets/Photography/Norway/Norway3.JPG';
import norway4 from './assets/Photography/Norway/Norway4.JPG';
// Norway - Compressed
import norway1Thumb from './assets/Photography/Norway/Norway1_thumb.JPG';
import norway2Thumb from './assets/Photography/Norway/Norway2_thumb.JPG';
import norway3Thumb from './assets/Photography/Norway/Norway3_thumb.JPG';
import norway4Thumb from './assets/Photography/Norway/Norway4_thumb.JPG';
// Yiwu - Originals
import yiwu1 from './assets/Photography/Yiwu/DSCF0337.JPG';
import yiwu2 from './assets/Photography/Yiwu/IMG_3635.JPG';
import yiwu3 from './assets/Photography/Yiwu/ISIMG-721924.JPG';
// Yiwu - Compressed
import yiwu1Thumb from './assets/Photography/Yiwu/DSCF0337_thumb.JPG';
import yiwu2Thumb from './assets/Photography/Yiwu/IMG_3635_thumb.JPG';
import yiwu3Thumb from './assets/Photography/Yiwu/ISIMG-721924_thumb.JPG';
import "./styles/App.css";
import "./styles/gallery.css";
import styled from 'styled-components';

const InstagramSection = styled.div`
  text-align: center;
  margin: 2rem auto 3rem;
  font-family: 'Inter', sans-serif;
  font-size: 1rem;
  max-width: 800px;
  padding: 0 1rem;
`;

const InstagramLink = styled.a`
  color: black;
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 2px;
  font-weight: 600;

  &:hover {
    color: #4a4a4a;
    opacity: 0.8;
  }
`;

const GalleryLayout = styled.div`
  display: flex;
  gap: 4rem;
  max-width: 1600px;
  margin: 0 auto;
  padding: 2rem;

  @media (max-width: 968px) {
    flex-direction: column;
    gap: 2rem;
  }
`;

const SidebarContainer = styled.div`
  width: 30%;
  position: sticky;
  top: 120px;
  height: fit-content;
  
  @media (max-width: 968px) {
    display: none;
  }
`;

const SidebarContent = styled.div`
  opacity: ${props => props.$isVisible ? 1 : 0};
  transition: opacity 0.5s ease-in-out;
`;

const SidebarTitle = styled.h2`
  font-family: 'Inter', sans-serif;
  font-size: 2.5rem;
  font-weight: 500;
  margin-bottom: 1.5rem;
  text-transform: lowercase;
  
  @media (max-width: 968px) {
    font-size: 2rem;
  }
`;

const SidebarDescription = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 1rem;
  line-height: 1.6;
  color: #555;
`;

const ContentContainer = styled.div`
  width: 70%;
  padding-bottom: 50vh;
  
  @media (max-width: 968px) {
    width: 100%;
    padding-bottom: 0;
  }
`;

const PortfolioSection = styled.div`
  margin-bottom: 8rem;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  @media (max-width: 968px) {
    margin-bottom: 4rem;
    
    &:last-child {
      margin-bottom: 2rem;
    }
  }
`;

const MobileSectionHeader = styled.div`
  display: none;
  
  @media (max-width: 968px) {
    display: block;
    margin-bottom: 2rem;
    
    h2 {
      font-family: 'Inter', sans-serif;
      font-size: 2rem;
      font-weight: 500;
      margin-bottom: 1rem;
      text-transform: lowercase;
    }
    
    p {
      font-family: 'Inter', sans-serif;
      font-size: 1rem;
      line-height: 1.6;
      color: #555;
    }
  }
`;

const Photography = () => {
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [activeSection, setActiveSection] = useState(0);
  const sectionRefs = useRef([]);

  // Handle scrolling to hash on page load
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      // Small delay to ensure DOM is fully rendered
      setTimeout(() => {
        const element = document.getElementById(hash.substring(1));
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, []);

  // Track which section is in view
  useEffect(() => {
    const observers = sectionRefs.current.map((section, index) => {
      if (!section) return null;
      
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setActiveSection(index);
          }
        },
        {
          root: null,
          rootMargin: '-20% 0px -60% 0px',
          threshold: 0
        }
      );
      
      observer.observe(section);
      return observer;
    });

    return () => {
      observers.forEach(observer => observer && observer.disconnect());
    };
  }, []);

  const portfolios = [
    {
      title: "new york",
      description: "the city that never sleeps, captured in fleeting moments of light and shadow",
      images: [
        { id: 1, imgSrc: ny1, imgThumb: ny1Thumb, alt: "New York 1" },
        { id: 2, imgSrc: ny2, imgThumb: ny2Thumb, alt: "New York 2" },
        { id: 3, imgSrc: ny3, imgThumb: ny3Thumb, alt: "New York 3" },
      ]
    },
    {
      title: "beijing",
      description: "where ancient traditions meet modern life, a city of contrasts and timeless beauty",
      images: [
        { id: 4, imgSrc: beijing1, imgThumb: beijing1Thumb, alt: "Beijing 1" },
        { id: 5, imgSrc: beijing2, imgThumb: beijing2Thumb, alt: "Beijing 2" },
      ]
    },
    {
      title: "norway",
      description: "nordic landscapes of raw natural beauty, where mountains touch clouds and fjords run deep",
      images: [
        { id: 6, imgSrc: norway1, imgThumb: norway1Thumb, alt: "Norway 1" },
        { id: 7, imgSrc: norway2, imgThumb: norway2Thumb, alt: "Norway 2" },
        { id: 8, imgSrc: norway3, imgThumb: norway3Thumb, alt: "Norway 3" },
        { id: 9, imgSrc: norway4, imgThumb: norway4Thumb, alt: "Norway 4" },
      ]
    },
    {
      title: "yiwu",
      description: "hometown memories, quiet streets and familiar faces frozen in time",
      images: [
        { id: 10, imgSrc: yiwu1, imgThumb: yiwu1Thumb, alt: "Yiwu 1" },
        { id: 11, imgSrc: yiwu2, imgThumb: yiwu2Thumb, alt: "Yiwu 2" },
        { id: 12, imgSrc: yiwu3, imgThumb: yiwu3Thumb, alt: "Yiwu 3" },
      ]
    },
  ];

  const handleCloseModal = () => {
    setSelectedPhoto(null);
  };

  const handleModalClick = (e) => {
    if (e.target === e.currentTarget) {
      handleCloseModal();
    }
  };

  return (
    <div className="App">
      <div className="Main">
        <InstagramSection>
          more photos coming soon... most photos and fun doodles are on{' '}
          <InstagramLink
            href="https://www.instagram.com/dandanbored"
            target="_blank"
            rel="noopener noreferrer"
          >
            instagram
          </InstagramLink>
        </InstagramSection>

        <GalleryLayout>
          {/* Sticky Sidebar */}
          <SidebarContainer>
            {portfolios.map((portfolio, index) => (
              <SidebarContent 
                key={index} 
                $isVisible={activeSection === index}
                style={{ 
                  position: activeSection === index ? 'relative' : 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%'
                }}
              >
                <SidebarTitle>{portfolio.title}</SidebarTitle>
                <SidebarDescription>{portfolio.description}</SidebarDescription>
              </SidebarContent>
            ))}
          </SidebarContainer>

          {/* Scrolling Content */}
          <ContentContainer>
            {portfolios.map((portfolio, portfolioIndex) => (
              <PortfolioSection 
                key={portfolioIndex}
                ref={el => sectionRefs.current[portfolioIndex] = el}
                id={portfolio.title.toLowerCase().replace(/\s+/g, '-')}
              >
                {/* Mobile: Show title/description above images */}
                <MobileSectionHeader>
                  <h2>{portfolio.title}</h2>
                  <p>{portfolio.description}</p>
                </MobileSectionHeader>
                
                <div className="gallery">
                  {portfolio.images.map((item) => (
                    <div
                      className="pics"
                      key={item.id}
                      onClick={() => setSelectedPhoto(item)}
                      style={{ cursor: 'pointer' }}
                    >
                      <img src={item.imgThumb} alt={item.alt} style={{ width: "100%" }} />
                    </div>
                  ))}
                </div>
              </PortfolioSection>
            ))}
          </ContentContainer>
        </GalleryLayout>

        {selectedPhoto && (
          <div className="modal" onClick={handleModalClick}>
            <div className="modal-content">
              <button className="close-button" onClick={handleCloseModal}>&times;</button>
              <img
                src={selectedPhoto.imgSrc}
                alt={selectedPhoto.alt}
                className="modal-image"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Photography;